import { useState } from "react";
import { Package, Truck, Clock, Phone, Mail, User, Smartphone, Laptop, Tablet, Gamepad2, HelpCircle, Send, CheckCircle, Loader2, Calendar, Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { SEO } from "@/components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const deviceTypes = [
  { value: "telephone", label: "Téléphone", icon: Smartphone },
  { value: "tablette", label: "Tablette", icon: Tablet },
  { value: "ordinateur", label: "Ordinateur", icon: Laptop },
  { value: "console", label: "Console", icon: Gamepad2 },
  { value: "autre", label: "Autre", icon: HelpCircle },
];

const timeSlots = [
  "10:00", "10:30", "11:00", "11:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
];

const statusLabels = {
  submitted: { label: "Demande reçue", color: "bg-blue-100 text-blue-800" },
  confirmed: { label: "Confirmé", color: "bg-indigo-100 text-indigo-800" },
  received: { label: "Appareil reçu", color: "bg-purple-100 text-purple-800" },
  diagnosed: { label: "Diagnostic effectué", color: "bg-yellow-100 text-yellow-800" },
  in_repair: { label: "En réparation", color: "bg-orange-100 text-orange-800" },
  ready: { label: "Prêt à récupérer", color: "bg-green-100 text-green-800" },
  picked_up: { label: "Récupéré", color: "bg-slate-100 text-slate-800" },
  cancelled: { label: "Annulé", color: "bg-red-100 text-red-800" },
};

export default function ClickCollectPage() {
  const [activeTab, setActiveTab] = useState("deposit"); // deposit or track
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    device_type: "",
    device_brand: "",
    device_model: "",
    issue_description: "",
    drop_off_date: "",
    drop_off_time: "",
    pickup_preference: "asap"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [trackingCode, setTrackingCode] = useState("");
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingError, setTrackingError] = useState("");
  const [isTracking, setIsTracking] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/click-collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error("Erreur lors de l'envoi");
      }

      const result = await response.json();
      setSubmitResult(result);
    } catch (err) {
      alert("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    setIsTracking(true);
    setTrackingError("");
    setTrackingResult(null);

    try {
      const response = await fetch(`${API_URL}/api/click-collect/track/${trackingCode}`);
      
      if (!response.ok) {
        throw new Error("Code de suivi non trouvé");
      }

      const result = await response.json();
      setTrackingResult(result);
    } catch (err) {
      setTrackingError("Code de suivi non trouvé. Vérifiez votre code et réessayez.");
    } finally {
      setIsTracking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="click-collect-page">
      <SEO
        title="Click & Collect Réparation à Niort - Déposez, On Répare, Récupérez"
        description="Service Click & Collect Mediastore Niort. Déposez votre iPhone, iPad, Mac, ordinateur, Samsung. Suivi en temps réel avec code de tracking. Techniciens certifiés Apple."
        keywords="click and collect réparation Niort, dépôt réparation Niort, suivi réparation en ligne Niort, click collect iPhone Niort, dépôt Mac Niort, tracking réparation Mediastore"
        path="/click-collect"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Click & Collect", path: "/click-collect" },
        ]}
      />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 md:py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-green-600/20 text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Package className="h-4 w-4" />
              Click & Collect
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Déposez, on répare,{" "}
              <span className="text-green-500">récupérez !</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Réservez un créneau pour déposer votre appareil, suivez sa réparation en temps réel, 
              et venez le récupérer quand il est prêt.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8">
        <div className="container-custom">
          <div className="flex justify-center gap-4 mb-8">
            <Button
              variant={activeTab === "deposit" ? "default" : "outline"}
              onClick={() => setActiveTab("deposit")}
              className={activeTab === "deposit" ? "bg-red-600 hover:bg-red-700" : ""}
              data-testid="tab-deposit"
            >
              <Package className="h-4 w-4 mr-2" />
              Déposer mon appareil
            </Button>
            <Button
              variant={activeTab === "track" ? "default" : "outline"}
              onClick={() => setActiveTab("track")}
              className={activeTab === "track" ? "bg-red-600 hover:bg-red-700" : ""}
              data-testid="tab-track"
            >
              <Search className="h-4 w-4 mr-2" />
              Suivre ma réparation
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16">
        <div className="container-custom">
          {activeTab === "deposit" && (
            <div className="max-w-2xl mx-auto">
              {submitResult ? (
                <Card className="text-center p-8" data-testid="submit-success">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    Demande enregistrée !
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Votre code de suivi :
                  </p>
                  <div className="bg-slate-100 rounded-xl p-4 mb-6">
                    <span className="text-3xl font-mono font-bold text-red-600" data-testid="tracking-code-result">
                      {submitResult.tracking_code}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">
                    Conservez ce code précieusement pour suivre votre réparation.
                    <br />Un email de confirmation vous a été envoyé.
                  </p>
                  <div className="bg-blue-50 rounded-lg p-4 text-left">
                    <h4 className="font-semibold text-blue-900 mb-2">📍 Prochaine étape</h4>
                    <p className="text-blue-800 text-sm">
                      Rendez-vous le <strong>{submitResult.drop_off_date}</strong> à <strong>{submitResult.drop_off_time}</strong> 
                      {" "}à notre boutique : <br />
                      <span className="flex items-center gap-1 mt-1">
                        <MapPin className="h-4 w-4" />
                        104 Rue de la Gare, 79000 Niort
                      </span>
                    </p>
                  </div>
                  <Button 
                    onClick={() => setSubmitResult(null)}
                    className="mt-6 bg-red-600 hover:bg-red-700"
                  >
                    Nouvelle demande
                  </Button>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8" data-testid="click-collect-form">
                  <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Package className="h-5 w-5 text-red-600" />
                    Formulaire de dépôt
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Personal Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="client_name" className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-500" />
                          Nom complet *
                        </Label>
                        <Input
                          id="client_name"
                          name="client_name"
                          required
                          placeholder="Jean Dupont"
                          value={formData.client_name}
                          onChange={handleChange}
                          className="h-12"
                          data-testid="cc-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="client_phone" className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-slate-500" />
                          Téléphone *
                        </Label>
                        <Input
                          id="client_phone"
                          name="client_phone"
                          type="tel"
                          required
                          placeholder="06 12 34 56 78"
                          value={formData.client_phone}
                          onChange={handleChange}
                          className="h-12"
                          data-testid="cc-phone"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="client_email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-500" />
                        Email *
                      </Label>
                      <Input
                        id="client_email"
                        name="client_email"
                        type="email"
                        required
                        placeholder="votre@email.com"
                        value={formData.client_email}
                        onChange={handleChange}
                        className="h-12"
                        data-testid="cc-email"
                      />
                    </div>

                    {/* Device Info */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Informations sur l&apos;appareil</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Type d&apos;appareil *</Label>
                          <Select value={formData.device_type} onValueChange={(v) => handleSelectChange("device_type", v)} required>
                            <SelectTrigger className="h-12" data-testid="cc-device-type">
                              <SelectValue placeholder="Sélectionnez" />
                            </SelectTrigger>
                            <SelectContent>
                              {deviceTypes.map((d) => (
                                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="device_brand">Marque *</Label>
                          <Input
                            id="device_brand"
                            name="device_brand"
                            required
                            placeholder="Apple, Samsung..."
                            value={formData.device_brand}
                            onChange={handleChange}
                            className="h-12"
                            data-testid="cc-brand"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="device_model">Modèle *</Label>
                          <Input
                            id="device_model"
                            name="device_model"
                            required
                            placeholder="iPhone 15, Galaxy S24..."
                            value={formData.device_model}
                            onChange={handleChange}
                            className="h-12"
                            data-testid="cc-model"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="issue_description">Description du problème *</Label>
                      <Textarea
                        id="issue_description"
                        name="issue_description"
                        required
                        placeholder="Décrivez le problème rencontré..."
                        value={formData.issue_description}
                        onChange={handleChange}
                        rows={4}
                        data-testid="cc-issue"
                      />
                    </div>

                    {/* Drop-off Info */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-red-600" />
                        Créneau de dépôt
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="drop_off_date">Date de dépôt *</Label>
                          <Input
                            id="drop_off_date"
                            name="drop_off_date"
                            type="date"
                            required
                            min={getMinDate()}
                            value={formData.drop_off_date}
                            onChange={handleChange}
                            className="h-12"
                            data-testid="cc-date"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Heure de dépôt *</Label>
                          <Select value={formData.drop_off_time} onValueChange={(v) => handleSelectChange("drop_off_time", v)} required>
                            <SelectTrigger className="h-12" data-testid="cc-time">
                              <SelectValue placeholder="Sélectionnez l'heure" />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlots.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 bg-red-600 hover:bg-red-700 text-lg font-semibold"
                      data-testid="cc-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Réserver mon créneau
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {activeTab === "track" && (
            <div className="max-w-xl mx-auto">
              <Card className="p-6 md:p-8">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Search className="h-5 w-5 text-red-600" />
                  Suivre ma réparation
                </h2>
                
                <form onSubmit={handleTrack} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tracking_code">Code de suivi</Label>
                    <Input
                      id="tracking_code"
                      placeholder="Ex: MC-A1B2C3D4"
                      value={trackingCode}
                      onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
                      className="h-14 text-center text-xl font-mono uppercase"
                      data-testid="tracking-input"
                    />
                  </div>
                  
                  {trackingError && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                      {trackingError}
                    </div>
                  )}
                  
                  <Button
                    type="submit"
                    disabled={isTracking || !trackingCode}
                    className="w-full h-12 bg-red-600 hover:bg-red-700"
                    data-testid="tracking-submit"
                  >
                    {isTracking ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Rechercher
                      </>
                    )}
                  </Button>
                </form>

                {trackingResult && (
                  <div className="mt-8 border-t pt-6" data-testid="tracking-result">
                    <div className="text-center mb-6">
                      <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${statusLabels[trackingResult.status]?.color || 'bg-slate-100'}`}>
                        {statusLabels[trackingResult.status]?.label || trackingResult.status}
                      </span>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Code</span>
                        <span className="font-mono font-bold">{trackingResult.tracking_code}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Appareil</span>
                        <span className="font-medium">{trackingResult.device_brand} {trackingResult.device_model}</span>
                      </div>
                      {trackingResult.estimated_price && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Devis estimé</span>
                          <span className="font-bold text-green-600">{trackingResult.estimated_price}€</span>
                        </div>
                      )}
                      {trackingResult.diagnosis_notes && (
                        <div className="py-2 border-b">
                          <span className="text-slate-600 block mb-1">Notes du technicien</span>
                          <p className="text-sm bg-slate-50 p-3 rounded">{trackingResult.diagnosis_notes}</p>
                        </div>
                      )}
                    </div>
                    
                    {trackingResult.status === "ready" && (
                      <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-bold text-green-800 mb-2">✅ Votre appareil est prêt !</h4>
                        <p className="text-green-700 text-sm">
                          Vous pouvez venir le récupérer à notre boutique pendant nos horaires d&apos;ouverture.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-slate-50">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-center mb-12">Comment ça marche ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: Package, title: "1. Réservez", desc: "Remplissez le formulaire et choisissez un créneau de dépôt" },
              { icon: Truck, title: "2. Déposez", desc: "Apportez votre appareil à la boutique au créneau choisi" },
              { icon: Clock, title: "3. On répare", desc: "Suivez l'avancement en temps réel avec votre code" },
              { icon: CheckCircle, title: "4. Récupérez", desc: "Venez chercher votre appareil réparé !" },
            ].map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
